import React, { useEffect, useState } from 'react';
import { getBlenderUrl } from '../../services/docServices';
import styles from './BlenderViewer.module.css';

function BlenderViewer({ file }) {
  const [blenderUrl, setBlenderUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let mounted = true;

    // Helper to normalize URL - but preserve localhost URLs in development
    const normalizeUrl = (url) => {
      if (!url) return url;
      
      // In development mode, preserve localhost URLs as they are needed for port-forwarding
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        // In development, keep localhost URLs as-is
        return url;
      } else {
        // In production, strip localhost/127.0.0.1 and port for proxy URLs
        if (url.includes('localhost') || url.includes('127.0.0.1')) {
          const fixed = url.replace(/(http|https):\/\/(localhost|127\.0\.0\.1):\d+/, '');
          return fixed;
        }
        return url;
      }
    };

    // If this is a Blender virtual file, fetch the correct blenderUrl if needed
    if (file && file.type === 'blender') {
      // Always extract server_name from file.location
      let server_name = null;
      if (file.location && typeof file.location === 'string') {
        const segments = file.location.split('/');
        if (segments.length > 0) {
          server_name = segments[segments.length - 1];
        }
      }
      if (server_name) {
        setLoading(true);
        getBlenderUrl(server_name)
          .then((resData) => {
            if (!mounted) return;
            if (resData.RetCode !== undefined && resData.RetCode !== 0) {
              throw new Error(resData.retmsg || 'Failed to get Blender server');
            }
            const dataObj = resData.data !== undefined ? resData.data : resData;
            if (!dataObj || !dataObj.url) {
              throw new Error('Invalid response format - missing Blender URL');
            }
            let fixedBlenderUrl = normalizeUrl(dataObj.url);
            setBlenderUrl(fixedBlenderUrl);
            setLoading(false);
          })
          .catch((err) => {
            if (mounted) {
              setError(err.message);
              setLoading(false);
            }
          });
      } else {
        setError('Could not extract server_name from file.location for Blender file.');
        setLoading(false);
      }
    } else if (file && file.url) {
      let fixedBlenderUrl = normalizeUrl(file.url);
      setBlenderUrl(fixedBlenderUrl);
      setLoading(false);
    } else {
      setError('No Blender server URL available.');
      setLoading(false);
    }

    return () => { mounted = false; };
  }, [retryCount, file]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && typeof event.data === 'string' && event.data.includes('websocket_error')) {
        console.error('Received WebSocket error from iframe:', event.data);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(prev => prev + 1);
  };

  if (loading) {
    return <div className={styles.loading}>Starting your Blender server, please wait...</div>;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>Error starting Blender: {error}</div>
        <div>
          <button 
            onClick={handleRetry}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!blenderUrl) {
    return <div className={styles.loading}>No Blender server URL available.</div>;
  }

  return (
    <iframe
      src={blenderUrl}
      className={styles.iframe}
      title="Blender Interface"
      allow="clipboard-read; clipboard-write"
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
    />
  );
}

export default BlenderViewer; 